import React from "react";
import { displayWeight } from "@/lib/utils";
import { OneExerciseCard } from "./OneExerciseCard";
import { useEffectiveUser } from "@/hooks/useEffectiveUser";
import { WorkoutModelJsonResource } from "@/types/workoutTypes";
import { SetModelJsonResource } from "@/types/setTypes";
import { WeightIcon, PencilIcon } from "lucide-react";
import WorkoutTitleModal from "./WorkoutTitleModal";

interface OneWorkoutCardProps {
  workout: WorkoutModelJsonResource;
  sets: SetModelJsonResource[];
}

export function OneWorkoutCard({ workout, sets }: OneWorkoutCardProps) {
  const { user, isLoading } = useEffectiveUser();
  const [modalOpen, setModalOpen] = React.useState(false);
  const [title, setTitle] = React.useState(workout.title);

  React.useEffect(() => {
    setTitle(workout.title);
  }, [workout.title]);

  if (isLoading) {
    return null;
  }
  return (
    <div className="mb-10">
      <button
        type="button"
        className="text-lg font-medium mb-2 text-tx-default text-left hover:underline focus:outline-none flex items-center justify-between w-full"
        style={{
          background: "none",
          border: 0,
          padding: 0,
          cursor: "pointer",
        }}
        onClick={() => setModalOpen(true)}
        aria-label="Edit workout title"
      >
        <span>{title}</span>
        <PencilIcon className="w-4 h-4 ml-2 text-tx-muted" />
      </button>

      <WorkoutTitleModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        workoutNanoId={workout.nano_id}
        initialTitle={title}
        onSuccess={(newTitle) => setTitle(newTitle)}
      />

      <div className="p-6 rounded-2xl shadow-xs bg-bg-light border border-bd-overlay">
        <div className="flex flex-col w-full">
          {/* Total volume row */}
          <div className="flex justify-center items-center mb-3 gap-2 bg-accent-bg-hot/10 rounded-sm  px-2 py-2">
            <span className="flex items-center text-base text-tx-default">
              <WeightIcon className="w-4 h-4 mr-2" />
              <span className="mr-1">Volume:</span>

              {displayWeight(
                workout.volume_lb ?? null,
                workout.volume_kg ?? null,
                user?.preferred_weight_unit ?? "lb"
              )}
            </span>
          </div>

          {/* Exercise list */}
          <div className="flex flex-col py-2 gap-4">
            {workout.exercises?.map((exercise) => (
              <OneExerciseCard
                key={exercise.nano_id}
                exercise={exercise}
                workout={workout}
                sets={sets}
                user={user}
              />
            ))}

            {/* 盖住最后一项训练的竖线底部 */}
            <div className="relative -mt-3 bg-bg-light">&nbsp;</div>
          </div>
        </div>
      </div>
    </div>
  );
}
